import React from 'react';
import {Tab} from '@headlessui/react';
import {useAuth} from '@/hooks/useAuth';
import {isUserAdmin} from '@/utils/tokenUtils';
import DashboardLayout from '../components/layout/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import {UserSearch} from '@/components/admin/UserSearch';
import {OrderSearch} from '@/components/admin/OrderSearch';
import {PhotoSearch} from '@/components/admin/PhotoSearch';
// import {UserKitVideos} from '@/components/admin/UserKitVideos'; // Hidden for now - will be developed later
import {PasswordReset} from '@/components/admin/PasswordReset';
import {RejectedEmails} from '@/components/admin/RejectedEmails';
import {AddTest} from '@/components/admin/AddTest';
import {AdminRecommendations} from '@/components/admin/AdminRecommendations';
import {AdminNotificationProvider} from '../contexts/AdminNotificationContext';
import {AdminNotificationBanner} from '../components/admin/AdminNotificationBanner';
import clsx from 'clsx';

interface Props {
    setPinnedMetrics?: (value: any) => void;
}

const Admin: React.FC<Props> = ({setPinnedMetrics = () => {}}) => {
    const {user} = useAuth();

    // Check if user has admin privileges
    if (!isUserAdmin(user?.token)) {
        return (
            <DashboardLayout>
                <div className="text-center py-16">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
                    <p className="text-gray-600">You do not have permission to access this page.</p>
                </div>
            </DashboardLayout>
        );
    }

    const tabs = [
        {name: 'Search For Users', component: UserSearch},
        {name: 'Search For Orders', component: OrderSearch},
        {name: 'User Insurance Photos', component: PhotoSearch},
        // {name: 'User Kit Videos', component: UserKitVideos}, // Hidden for now - will be developed later
        {name: 'Manual Password Reset', component: PasswordReset},
        {name: 'Rejected Emails', component: RejectedEmails},
        {name: 'Add Test', component: AddTest},
        {name: 'Recommendations', component: AdminRecommendations},
    ];

    return (
        <DashboardLayout>
            <AdminNotificationProvider>
                <PageHeader title="Admin Dashboard" subtitle="System administration and management tools" />

                {/* Global Admin Notifications */}
                <AdminNotificationBanner className="mb-6" />

                <Tab.Group>
                    <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-8">
                        {tabs.map((tab) => (
                            <Tab
                                key={tab.name}
                                className={({selected}) =>
                                    clsx(
                                        'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                                        'ring-white ring-opacity-60 ring-offset-2 ring-offset-gray-400 focus:outline-none focus:ring-2',
                                        selected
                                            ? 'bg-white text-gray-900 shadow'
                                            : 'text-gray-700 hover:bg-white/50 hover:text-gray-900'
                                    )
                                }>
                                {tab.name}
                            </Tab>
                        ))}
                    </Tab.List>

                    <Tab.Panels>
                        <Tab.Panel>
                            <UserSearch setPinnedMetrics={setPinnedMetrics} />
                        </Tab.Panel>
                        <Tab.Panel>
                            <OrderSearch />
                        </Tab.Panel>
                        <Tab.Panel>
                            <PhotoSearch />
                        </Tab.Panel>
                        {/* <Tab.Panel>
                            <UserKitVideos />
                        </Tab.Panel> */}
                        <Tab.Panel>
                            <PasswordReset />
                        </Tab.Panel>
                        <Tab.Panel>
                            <RejectedEmails />
                        </Tab.Panel>
                        <Tab.Panel>
                            <AddTest />
                        </Tab.Panel>
                        <Tab.Panel>
                            <AdminRecommendations />
                        </Tab.Panel>
                    </Tab.Panels>
                </Tab.Group>
            </AdminNotificationProvider>
        </DashboardLayout>
    );
};

export default Admin;
